import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IonCard, IonIcon } from "@ionic/react";
import "./BiWazeTherapyCard.css";

// Import SVG icons
import { chevronForward } from "ionicons/icons";

// Types for the component
interface TherapyDay {
  active: boolean;
  partial?: number;
  error?: string;
  dayOfMonth?: number; // For month view
  date?: string; // ISO date string
}

export interface MonthlyData {
  date: string; // Format: "YYYY-MM"
  label: string; // Display format: "Jan 25"
  active: boolean; // Whether there was activity in this period
  partial: number; // Percentage completion (0-100)
  value: number; // Numeric value
  sessions: number; // Number of therapy sessions
  isMonthly: boolean; // Flag indicating monthly aggregation
}

export interface TherapyItem {
  name: string;
  type: "clear" | "cough";
  value: string;
  unit: string;
  days: TherapyDay[];
  hadConnectedDevice?: boolean;
  error?:
  | { type: string; message: string; hasAlert?: boolean }
  | boolean
  | undefined;
  monthData?: MonthlyData[]; // For year view
  notification?: any;
  hasPairedDevice?: boolean;
}

export interface BiWazeTherapyCardProps {
  title?: string;
  therapies: TherapyItem[];
  hideFooter?: boolean;
  name?: string;
  initials?: string;
  avatarColor?: string;
  avatar?: string;
  adherenceHeader?: string; // e.g., ""
  showHeader?: boolean;
  timePeriod?: "Week" | "Month" | "Year"; // Add time period support
  currentMonth?: string; // e.g., "April"
  currentYear?: number; // e.g., 2025
  onCardClick?: (deviceType?: "cough" | "clear") => void;
  onAvatarClick?: () => void;
  onArrowClick?: () => void;
  onLikeClick?: () => void;
  onCommentClick?: () => void;
  onBookmarkClick?: () => void;
  onCloseAlert?: () => void;
  todaysData?: boolean; // Flag to indicate if showing today's data
  oldestKnownTherapyDate?: { cough: string; clear: string }; // e.g., "2022-01-01" for oldest known therapy day
  onDateClick?: (date: string) => void; // New prop for handling date clicks in Month view
}

const BiWazeTherapyCard: React.FC<BiWazeTherapyCardProps> = ({
  title,
  therapies,
  hideFooter = false,
  name,
  initials,
  avatar,
  avatarColor,
  showHeader = true,
  timePeriod,
  currentMonth = "Month",
  adherenceHeader,
  currentYear = new Date().getFullYear(),
  todaysData = false,
  onCardClick,
  onCloseAlert,
  oldestKnownTherapyDate,
  onDateClick,
}) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const getViewportWidth = () => {
    if (typeof window === "undefined") return 1024;
    return Math.round(window.visualViewport?.width || window.innerWidth);
  };

  const [viewportWidth, setViewportWidth] = useState(
    getViewportWidth()
  );
  const [cardWidth, setCardWidth] = useState(0);

  useLayoutEffect(() => {
    const nextWidth = Math.round(cardRef.current?.getBoundingClientRect().width || 0);
    if (nextWidth > 0) {
      setCardWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
    }
  }, []);

  useEffect(() => {
    const element = cardRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      const nextWidth = Math.round(element.getBoundingClientRect().width || 0);
      setCardWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let frameId = 0;
    const handleResize = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        setViewportWidth(getViewportWidth());
      });
    };

    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("scroll", handleResize);
    handleResize();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("scroll", handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    const latestWidth = getViewportWidth();
    if (latestWidth !== viewportWidth) {
      setViewportWidth(latestWidth);
    }
  });

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const cyclesSummaryheader = "YTD (monthly avg.)";
  const measuredWidth = cardWidth > 0 ? cardWidth : viewportWidth;
  const isLargeScreen = measuredWidth >= 600;
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

  // Helper function to check if a day is in the future
  const isFutureDay = (day: TherapyDay) => {
    if (!day.date) return false;

    // Parse as local date without timezone shift
    const [year, month, dayOfMonth] = day.date.split("-").map(Number);
    const dayDate = new Date(year, month - 1, dayOfMonth); // Local midnight

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    return dayDate > currentDate;
  };

  // Helper function to check if a month is in the future (for Year view)
  const isFutureMonth = (month: TherapyDay) => {
    if (!month.date) return false;

    // Parse month date in "YYYY-MM" format
    const [year, monthNum] = month.date.split("-").map(Number);
    const monthDate = new Date(year, monthNum - 1, 1); // First day of the month

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    return monthDate > currentDate;
  };
  const isBeforeEarliestKnownTherapyDay = (
    day: TherapyDay,
    therapyType: "cough" | "clear",
    checkMonth = false
  ) => {
    if (!day.date) return false;
    // Parse both dates in local timezone
    const dayDate = new Date(day.date + "T00:00:00");
    const oldestDateString = oldestKnownTherapyDate
      ? oldestKnownTherapyDate[therapyType]
      : "2022-01-01";
    const oldestKnownTherapyDateObj = new Date(oldestDateString + "T00:00:00");

    if (checkMonth) {
      const dayYear = dayDate.getFullYear();
      const dayMonth = dayDate.getMonth();
      const oldestYear = oldestKnownTherapyDateObj.getFullYear();
      const oldestMonth = oldestKnownTherapyDateObj.getMonth();
      // Compare year first, then month
      if (dayYear !== oldestYear) {
        return dayYear < oldestYear;
      }
      return dayMonth < oldestMonth;
    }

    return dayDate < oldestKnownTherapyDateObj;
  };

  // Size configuration based on screen size
  const circleSize = isLargeScreen ? 35 : 20;
  const radius = isLargeScreen ? 15 : 8.5;
  const strokeWidth = isLargeScreen ? 4.5 : 3;

  // ViewBox should accommodate the circle + stroke width
  const viewBoxSize = isLargeScreen ? 40 : 20;
  const renderWeekCircles = (therapy: TherapyItem) => {
    const days = ["M", "T", "W", "T", "F", "S", "S"];
    const dayStatus = therapy.days;
    const therapyColorClass =
      therapy.type === "clear" ? "biwaze-clear-circle" : "biwaze-cough-circle";

    return (
      <div className="biwaze-week-circles">
        {days.map((day, index) => {
          const status = dayStatus[index] || { active: false };
          let circleClass = "biwaze-inactive-circle";
          let opacity = "1";

          if (status.active) {
            circleClass = therapyColorClass;
            opacity = status.partial ? "0.5" : "1";
            // if (status.error) circleClass = "biwaze-error-circle";
          }

          const isGreyedOut = isFutureDay(status) || isBeforeEarliestKnownTherapyDay(status, therapy.type);
          return (
            <div
              key={index}
              className="biwaze-day-column"
              onClick={() => {
                if (!isGreyedOut && status.date && onDateClick) {
                  onDateClick(status.date);
                }
              }}
              style={{ cursor: !isGreyedOut && status.date && onDateClick ? "pointer" : "default" }}
            >
              {isFutureDay(status) ||
                isBeforeEarliestKnownTherapyDay(status, therapy.type) ? (
                <div
                  className="biwaze-empty-grey-circle"
                  style={{ width: circleSize, height: circleSize }}
                />
              ) : (
                <svg
                  width={circleSize}
                  height={circleSize}
                  viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {status.active && status.partial ? (
                    <>
                      <circle
                        cx={viewBoxSize / 2}
                        cy={viewBoxSize / 2}
                        r={radius}
                        className="biwaze-inactive-circle"
                        strokeWidth={strokeWidth}
                      />
                      <circle
                        cx={viewBoxSize / 2}
                        cy={viewBoxSize / 2}
                        r={radius}
                        className={circleClass}
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${2 * Math.PI * radius * (status.partial / 100)
                          } ${2 * Math.PI * radius * (1 - status.partial / 100)}`}
                        strokeDashoffset={2 * Math.PI * radius * 0.25}
                        transform={`rotate(-90 ${viewBoxSize / 2} ${viewBoxSize / 2
                          })`}
                      />
                    </>
                  ) : (
                    <circle
                      cx={viewBoxSize / 2}
                      cy={viewBoxSize / 2}
                      r={radius}
                      className={circleClass}
                      strokeOpacity={opacity}
                      strokeWidth={strokeWidth}
                    />
                  )}
                </svg>
              )}
              <span className="biwaze-day-label">{day}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMonthCalendar = (therapy: TherapyItem) => {
    const dayStatus = therapy.days;
    const therapyColorClass =
      therapy.type === "clear" ? "biwaze-clear-circle" : "biwaze-cough-circle";

    // Create proper calendar grid starting from Monday
    const firstDayData = dayStatus.find(
      (day) => day.date && new Date(day.date).getDate() === 1
    );

    if (!firstDayData || !firstDayData.date) {
      // Fallback: create simple grid without proper calendar layout
      const weeks: (TherapyDay | null)[][] = [];
      for (let i = 0; i < dayStatus.length; i += 7) {
        const week = dayStatus.slice(i, i + 7);
        // Pad week to 7 days if needed
        while (week.length < 7) {
          week.push(null as any);
        }
        weeks.push(week);
      }

      return (
        <div className="biwaze-month-calendar">
          {/* Day headers - M T W T F S S */}
          <div className="biwaze-calendar-header">
            {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => (
              <div key={index} className="biwaze-day-header">
                {day}
              </div>
            ))}
          </div>

          {/* Add Month Name here */}
          <div className="biwaze-period-display">{getDateDisplay()}</div>

          {/* Calendar weeks */}
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="biwaze-calendar-week">
              {week.map((day, dayIndex) => (
                <div key={dayIndex} className="biwaze-calendar-day">
                  {day ? (
                    isFutureDay(day) ||
                      isBeforeEarliestKnownTherapyDay(day, therapy.type) ? (
                      <div className="biwaze-empty-grey-circle">
                        {day.dayOfMonth}
                      </div>
                    ) : (
                      renderDayCircle(
                        day,
                        `${weekIndex}-${dayIndex}`,
                        therapyColorClass
                      )
                    )
                  ) : (
                    <div className="biwaze-empty-day"></div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    // Get first day of month and calculate offset for Monday start
    const firstDate = new Date(firstDayData.date);
    const firstDayOfWeek = firstDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const mondayOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Convert to Monday start (0 = Monday)

    // Create calendar grid
    const calendarGrid: (TherapyDay | null)[] = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < mondayOffset; i++) {
      calendarGrid.push(null);
    }

    // Add all month days (sorted by dayOfMonth to ensure proper order)
    const sortedDays = [...dayStatus].sort(
      (a, b) => (a.dayOfMonth || 0) - (b.dayOfMonth || 0)
    );
    calendarGrid.push(...sortedDays);

    // Group into weeks (7 days each)
    const weeks: (TherapyDay | null)[][] = [];
    for (let i = 0; i < calendarGrid.length; i += 7) {
      const week = calendarGrid.slice(i, i + 7);
      // Ensure each week has exactly 7 days
      while (week.length < 7) {
        week.push(null);
      }
      weeks.push(week);
    }

    return (
      <div className="biwaze-month-calendar">
        {/* Calendar weeks */}
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="biwaze-calendar-week">
            {week.map((day, dayIndex) => (
              <div key={dayIndex} className="biwaze-calendar-day">
                {day ? (
                  isFutureDay(day) ||
                    isBeforeEarliestKnownTherapyDay(day, therapy.type) ? (
                    <div className="biwaze-empty-grey-circle"></div>
                  ) : (
                    renderDayCircle(
                      day,
                      `${weekIndex}-${dayIndex}`,
                      therapyColorClass
                    )
                  )
                ) : (
                  <div className="biwaze-empty-day"></div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // Add this function before renderMonthCalendar
  const renderDayCircle = (
    day: TherapyDay,
    key: string,
    therapyColorClass: string
  ) => {
    let circleClass = "biwaze-inactive-circle";
    let opacity = "1";

    if (day.active) {
      circleClass = therapyColorClass;
      opacity = day.partial ? "0.5" : "1";
      // if (day.error) circleClass = "biwaze-error-circle";
    }

    return (
      <div
        className="biwaze-day-circle-wrapper align-center justify-center items-center"
        key={key}
        onClick={() => {
          if (day.date && onDateClick ) {
            onDateClick(day.date);
          }
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          cursor: day.date && onDateClick ? "pointer" : "default"
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <svg
            width={circleSize}
            height={circleSize}
            viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {day.active && day.partial ? (
              <>
                <circle
                  cx={viewBoxSize / 2}
                  cy={viewBoxSize / 2}
                  r={radius}
                  className="biwaze-inactive-circle"
                  strokeWidth={strokeWidth}
                />
                <circle
                  cx={viewBoxSize / 2}
                  cy={viewBoxSize / 2}
                  r={radius}
                  className={circleClass}
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${
                    2 * Math.PI * radius * (day.partial / 100)
                    } ${2 * Math.PI * radius * (1 - day.partial / 100)}`}
                  strokeDashoffset={2 * Math.PI * radius * 0.25}
                  transform={`rotate(-90 ${viewBoxSize / 2} ${viewBoxSize / 2})`}
                />
              </>
            ) : (
              <circle
                cx={viewBoxSize / 2}
                cy={viewBoxSize / 2}
                r={radius}
                className={circleClass}
                strokeOpacity={opacity}
                strokeWidth={strokeWidth}
              />
            )}
          </svg>
        </div>
        {day && day.date && (
          <span
            className="biwaze-day-number font-medium"
            style={{ fontSize: isLargeScreen ? "1rem" : "0.8rem", lineHeight: isLargeScreen ? "1rem" : "0.8rem" }}
          >
            {(() => {
              if (!day.date) return "";
              const [year, month, dayNum] = day.date.split("-");
              const monthLabel = monthNames[Number(month) - 1] || "";
              return `${isLargeScreen ? monthLabel : ""} ${Number(dayNum)}`;
            })()}
          </span>
        )}
      </div>
    );
  };

  const renderYearMonths = (therapy: TherapyItem) => {
    const monthData = therapy.days || [];
    const therapyColorClass =
      therapy.type === "clear" ? "biwaze-clear-circle" : "biwaze-cough-circle";

    // Group months into quarters (3 months each)
    const quarters: any[][] = [];
    for (let i = 0; i < monthData.length; i += 4) {
      quarters.push(monthData.slice(i, i + 4));
    }

    return (
      <div className="biwaze-year-months">
        {quarters.map((quarter, quarterIndex) => (
          <div key={quarterIndex} className="biwaze-quarter-row">
            {quarter.map((month, monthIndex) => {
              let circleClass = "biwaze-inactive-circle";
              let opacity = "1";

              if (month.active) {
                circleClass = therapyColorClass;
                opacity = month.partial ? "0.5" : "1";
              }

              const isMonthGreyedOut = isFutureMonth(month) || isBeforeEarliestKnownTherapyDay(month, therapy.type, true);
              return (
                <div
                  key={monthIndex}
                  className="biwaze-month-column flex flex-col items-center"
                  onClick={() => {
                    if (!isMonthGreyedOut && month.date && onDateClick) {
                      onDateClick(month.date);
                    }
                  }}
                  style={{ cursor: !isMonthGreyedOut && month.date && onDateClick ? "pointer" : "default" }}
                >
                  {isFutureMonth(month) ||
                    isBeforeEarliestKnownTherapyDay(month, therapy.type, true) ? (
                    <div
                      className="biwaze-empty-grey-circle"
                      style={{ width: circleSize, height: circleSize }}
                    />
                  ) : (
                    <svg
                      width={circleSize}
                      height={circleSize}
                      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {month.active && month.partial ? (
                        <>
                          <circle
                            cx={viewBoxSize / 2}
                            cy={viewBoxSize / 2}
                            r={radius}
                            className="biwaze-inactive-circle"
                            strokeWidth={strokeWidth}
                          />
                          <circle
                            cx={viewBoxSize / 2}
                            cy={viewBoxSize / 2}
                            r={radius}
                            className={circleClass}
                            strokeWidth={strokeWidth}
                            strokeDasharray={`${2 * Math.PI * radius * (month.partial / 100)
                              } ${
                                2 * Math.PI * radius * (1 - month.partial / 100)
                              }`}
                            strokeDashoffset={2 * Math.PI * radius * 0.25}
                            transform={`rotate(-90 ${viewBoxSize / 2} ${
                              viewBoxSize / 2
                              })`}
                          />
                        </>
                      ) : (
                        <circle
                          cx={viewBoxSize / 2}
                          cy={viewBoxSize / 2}
                          r={radius}
                          className={circleClass}
                          strokeOpacity={opacity}
                          strokeWidth={strokeWidth}
                        />
                      )}
                    </svg>
                  )}
                  <span className="biwaze-month-label">
                    {isLargeScreen
                      ? // Large screens: show full month name
                      month.label && /^[A-Za-z]{3}/.test(month.label)
                        ? month.label.replace(/\d+|\s+/g, "")
                        : month.date && /^[A-Za-z]{3}/.test(month.label)
                          ? (() => {
                            const [year, monthNum] = month.date
                              .split("-")
                              .map(Number);
                            const date = new Date(year, monthNum - 1, 1);
                            return date.toLocaleString("default", {
                              month: "long",
                            });
                          })()
                          : ""
                      : // Small screens: show short month name
                      month.label && /^[A-Za-z]{3}/.test(month.label)
                        ? month.label.replace(/\d+|\s+/g, "")
                        : month.date && /^[A-Za-z]{3}/.test(month.label)
                          ? (() => {
                            const [year, monthNum] = month.date
                              .split("-")
                              .map(Number);
                            const date = new Date(year, monthNum - 1, 1);
                            return date.toLocaleString("default", {
                              month: "short",
                            });
                          })()
                          : ""}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const renderCircles = (therapy: TherapyItem) => {
    switch (timePeriod) {
      case "Week":
        return renderWeekCircles(therapy);
      case "Month":
        return renderMonthCalendar(therapy);
      case "Year":
        return renderYearMonths(therapy);
      default:
        return renderWeekCircles(therapy);
    }
  };

  const getDateDisplay = () => {
    switch (timePeriod) {
      case "Week":
        return "today";
      case "Month":
        return currentMonth;
      case "Year":
        return currentYear?.toString();
      default:
        return "today";
    }
  };

  const getTitle = () => {
    switch (timePeriod) {
      case "Month":
        return "Monthly Adherence";
      case "Year":
        return "Yearly Adherence";
      default:
        return title;
    }
  };

  return (
    <IonCard ref={cardRef} className="biwaze-therapy-card">
      {showHeader && (
        <div
          className="biwaze-summary-title"
          onClick={() => {
            onCardClick && onCardClick("cough");
          }}
        >
          <div className="">
            <h2 className="text-xl text-black font-medium">
              {name || getTitle()}
            </h2>
          </div>
          {onCardClick && (
            <div
              className="biwaze-arrow clickable"
              onClick={() => {
                onCardClick && onCardClick("cough");
              }}
            >
              <IonIcon icon={chevronForward} className="w-6 h-6" />
            </div>
          )}
          {(timePeriod === "Month" || timePeriod === "Year") && (
            <div className="biwaze-period-display">{getDateDisplay()}</div>
          )}
        </div>
      )}
      {therapies.length === 0 && (
        <div className="no-data">No therapy data available.</div>
      )}
      {(therapies || []).map((therapy, index) => (
        <React.Fragment key={index}>
          {/* {therapy?.notification && timePeriod !== "Year" && (
            <div
              className={`notification-banner ${therapy?.notification?.type}`}
              style={{ position: "relative" }}
            >
              <IonIcon icon={informationCircleOutline} />
              <span>{therapy.notification?.message || ""}</span>
              {onCloseAlert && (
                <IonIcon
                  icon={closeOutline}
                  onClick={() => {
                    // Close notification on click
                    therapy.notification = null;
                    onCloseAlert && onCloseAlert();
                    // If you want to trigger a re-render, you may need to use state for notifications
                  }}
                  style={{
                    position: "absolute",
                    // top: "8px",
                    right: "8px",
                    cursor: "pointer",
                    fontSize: "20px",
                  }}
                />
              )}
            </div>
          )} */}
          {adherenceHeader && (
            <div className="patient-name">{adherenceHeader}</div>
          )}
          <div
            className="biwaze-therapy-item"
            onClick={() => {
              onCardClick && onCardClick(therapy.type);
            }}
          >
            <div className="biwaze-therapy-content">
              {/* 30% */}
              <div
                className="biwaze-therapy-details justify-center"
                style={{ flex: "0 0 30%" }}
              >
                {todaysData && (
                  <div
                    className={`biwaze-therapy-name ${therapy.type} ${therapy.hasPairedDevice ? "br-2 paired-device-border" : ""
                      }`}
                  >
                    {therapy.name}
                  </div>
                )}
                <div className="flex items-baseline">
                  <span
                    className={`biwaze-therapy-value font-bold ${typeof therapy.value === "string"
                        ? "text-3xl max-w-[250px]"
                        : "text-5xl"
                      }`}
                    style={{
                      fontSize: "24px",
                      lineHeight: "1",
                      color: "#504f4e",
                      overflow:
                        typeof therapy.value === "string"
                          ? "hidden"
                          : "visible",
                      textOverflow:
                        typeof therapy.value === "string"
                          ? "ellipsis"
                          : "initial",
                      whiteSpace:
                        typeof therapy.value === "string" ? "nowrap" : "normal",
                      display: "inline-block",
                    }}
                  >
                    {therapy.value}
                  </span>{" "}
                  <span className="biwaze-therapy-unit ml-1">
                    <span style={{ color: "black" }}> {therapy.unit}</span>
                  </span>
                </div>
                {todaysData && (
                  <div
                    className="biwaze-therapy-date"
                    style={{
                      color: "#504f4e",
                    }}
                  >
                    {getDateDisplay()}
                  </div>
                )}
                {!todaysData && (
                  <div className={`biwaze-therapy-name ${therapy.type}`}>
                    {therapy.name}
                  </div>
                )}
              </div>
              {/* 70% */}
              <div className="biwaze-therapy-circles">
                {(timePeriod === "Year" || timePeriod === "Month") && (
                  <div
                    className="font-medium mb-2"
                    style={{
                      fontSize: isLargeScreen ? "18px" : "14px",
                      lineHeight: isLargeScreen ? "22px" : "18px",
                      height: isLargeScreen ? "22px" : "18px",
                      letterSpacing: "0.35px",
                      color: "#586670",
                    }}
                  >
                    {timePeriod === "Year"
                      ? cyclesSummaryheader
                      : getDateDisplay()}
                  </div>
                )}
                {renderCircles(therapy)}
              </div>
            </div>
          </div>
        </React.Fragment>
      ))}
    </IonCard>
  );
};

export default BiWazeTherapyCard;
